import { useMemo } from 'react'
import { useAppStore } from '../store/useAppStore'
import { DEFAULT_PACKAGE_FEATURE_FLAGS, PackageFeatureFlags } from '../types'

type FeatureFlagValue = unknown

const TRUTHY_STRINGS = new Set(['true', '1', 'yes', 'on', 'enabled'])

function coerceBoolean(value: FeatureFlagValue, fallback: boolean): boolean {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value !== 0
  }
  if (typeof value === 'string') {
    return TRUTHY_STRINGS.has(value.trim().toLowerCase())
  }
  if (value == null) {
    return fallback
  }
  return Boolean(value)
}

export function getFeatureFlagsState(): PackageFeatureFlags {
  return useAppStore.getState().featureFlags ?? { ...DEFAULT_PACKAGE_FEATURE_FLAGS }
}

export function getFeatureFlagValue<T = unknown>(key: string, defaultValue: T | null = null): T | null {
  const state = getFeatureFlagsState()
  const value = state.features?.[key]
  if (value === undefined) {
    return defaultValue
  }
  return value as T
}

export function isFeatureEnabled(key: string, defaultValue: boolean = false): boolean {
  const value = getFeatureFlagValue(key, undefined)
  if (value === undefined) {
    return defaultValue
  }
  return coerceBoolean(value, defaultValue)
}

export function getModuleFlags(): Record<string, boolean> {
  const state = getFeatureFlagsState()
  const modules = state.features?.modules
  return (modules && typeof modules === 'object' ? (modules as Record<string, boolean>) : {}) ?? {}
}

export function isModuleEnabled(moduleKey: string, defaultValue: boolean = false): boolean {
  const modules = getModuleFlags()
  if (modules[moduleKey] === undefined) {
    return defaultValue
  }
  return Boolean(modules[moduleKey])
}

export function useFeatureFlags() {
  return useAppStore((state) => state.featureFlags)
}

export function useFeatureFlag(key: string, defaultValue: boolean = false): boolean {
  const flagValue = useAppStore((state) => {
    const featureFlags = state.featureFlags?.features ?? {}
    return featureFlags[key]
  })

  return useMemo(() => {
    if (flagValue === undefined) {
      return defaultValue
    }
    return coerceBoolean(flagValue, defaultValue)
  }, [flagValue, defaultValue])
}

export function useModuleFlag(moduleKey: string, defaultValue: boolean = false): boolean {
  const moduleValue = useAppStore((state) => {
    const modules = state.featureFlags?.features?.modules
    if (!modules || typeof modules !== 'object') return undefined
    return (modules as Record<string, boolean>)[moduleKey]
  })

  return useMemo(() => {
    if (moduleValue === undefined) {
      return defaultValue
    }
    return Boolean(moduleValue)
  }, [moduleValue, defaultValue])
}
